import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { useVoice } from "../../context/Voice/VoiceContext";
import * as apiVoice from "../../services/apiVoice";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  row: { display: "flex", gap: 8, marginBottom: 8, alignItems: "center" },
  item: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "6px 10px",
    marginBottom: 6,
    fontSize: 13,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  script: {
    fontSize: 13,
    lineHeight: 1.6,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f6faf7",
    borderRadius: 8,
    padding: "8px 10px",
    marginTop: 8,
  },
  empty: { fontSize: 12, color: theme.palette.text.secondary },
}));

const STATUS_COLOR = {
  pending: { background: "#faeeda", color: "#633806" },
  promised: { background: "#e6f1fb", color: "#0c447c" },
  paid: { background: "#eaf3de", color: "#27500a" },
};

const VoiceCollection = ({ contactId, ticketId }) => {
  const classes = useStyles();
  const { status } = useVoice();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [script, setScript] = useState("");

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const data = await apiVoice.listCollections(contactId);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      /* 403 when disabled */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (status && status.enabled && status.paymentAssistEnabled) load();
  }, [status, load]);

  if (!status || !status.enabled || !status.paymentAssistEnabled) return null;

  const create = async () => {
    try {
      await apiVoice.createCollection({
        contactId,
        ticketId,
        amountDue: amount ? Number(amount) : undefined,
        reason,
      });
      setAmount("");
      setReason("");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const genScript = async () => {
    try {
      const res = await apiVoice.getCollectionScript({
        amountDue: amount ? Number(amount) : undefined,
        methodsAllowed: ["card", "transfer"],
      });
      setScript(res.script || "");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div>
      <div className={classes.row}>
        <TextField
          size="small"
          variant="outlined"
          label={i18n.t("voice.collection.amountDue")}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ width: 110 }}
        />
        <TextField
          size="small"
          variant="outlined"
          label={i18n.t("voice.collection.reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ flex: 1 }}
        />
      </div>
      <div className={classes.row}>
        <Button size="small" variant="outlined" onClick={create}>
          {i18n.t("voice.collection.create")}
        </Button>
        <Button size="small" onClick={genScript}>
          {i18n.t("voice.collection.generateScript")}
        </Button>
      </div>

      {script && <div className={classes.script}>{script}</div>}

      {loading && !items.length ? (
        <CircularProgress size={18} />
      ) : items.length ? (
        items.map((c) => (
          <div key={c.id} className={classes.item}>
            <span>
              {c.amountDue || ""} {c.currency} {c.reason ? `· ${c.reason}` : ""}
            </span>
            <Chip
              size="small"
              label={c.paymentStatus}
              style={STATUS_COLOR[c.paymentStatus] || {}}
            />
          </div>
        ))
      ) : (
        <div className={classes.empty}>{i18n.t("voice.collection.empty")}</div>
      )}
    </div>
  );
};

export default VoiceCollection;
